import Vue from 'vue'
import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import rootStore from '@vue-storefront/core/store'
import i18n from '@vue-storefront/i18n'
import { adjustMultistoreApiUrl } from '@vue-storefront/core/lib/multistore'
import RootState from '@vue-storefront/core/types/RootState'
import UserState from '../types/UserState'
import { Logger } from '@vue-storefront/core/lib/logger'
import { TaskQueue } from '@vue-storefront/core/lib/sync'
import { UserProfile } from '../types/UserProfile'
import { isServer, processURLAddress } from '@vue-storefront/core/helpers'
import config from 'config'
import getApiEndpointUrl from '@vue-storefront/core/helpers/getApiEndpointUrl'
import { request as customerRequest } from '@vue-storefront/core/lib/customerRequest'
import clearRuntimeCaches from '@vue-storefront/core/service-worker/clearRuntimeCaches'
import { withRefreshTokenCookie, writeAccessTokenCookie, removeAccessTokenCookie } from '../helpers/refreshTokenCookie'
import {
  customerAuthHeaders,
  endSessionGeneration,
  isDefinitiveRefreshCode,
  isOtherCustomer,
  loginGeneration,
  reloadPage,
  startLoginGeneration,
  refreshFailure,
  sessionGeneration,
  sharedRefresh,
  withRefreshLock
} from '../helpers/customerSession'

var fetch = require('isomorphic-fetch')

// K2-3138: in the browser vuestorefront-api keeps the refresh token in the HttpOnly rut cookie.
function refreshTokenTransport (init) {
  return isServer ? init : withRefreshTokenCookie(init)
}

export const LOGOUT_REVOKE_TIMEOUT_MS = 3000
// A hung refresh would hold the cross-tab lock and block every tab (K2-3138).
export const REFRESH_TIMEOUT_MS = 15000
// A lost refresh: the winning tab may write its cut a moment after this tab's 400 (K2-3138).
const WINNER_COOKIE_WAIT_MS = 500

/** K2-3138: revokes the session in ms-api; vuestorefront-api also clears the rut cookie. Never rejects. */
function revokeSession (token, timeoutMs = 0) {
  let url = getApiEndpointUrl(config.users, 'logout_endpoint')
  if (config.storeViews.multistore) {
    url = adjustMultistoreApiUrl(url)
  }
  const controller = timeoutMs && typeof AbortController !== 'undefined' ? new AbortController() : null
  const revoked = fetch(processURLAddress(url), withRefreshTokenCookie({
    method: 'POST',
    mode: 'cors',
    headers: Object.assign({
      'Accept': 'application/json, text/plain, */*',
      'Content-Type': 'application/json'
    }, customerAuthHeaders(token)),
    ...(controller ? { signal: controller.signal } : {})
  })).catch(err => Logger.error('Logout request failed: ' + err, 'user')())
  if (!timeoutMs) {
    return revoked
  }
  let timer
  const timedOut = new Promise(resolve => {
    timer = setTimeout(() => {
      if (controller) controller.abort() // a late answer must not clear a newer session's rut
      resolve()
    }, timeoutMs)
  })
  return Promise.race([revoked, timedOut]).then(() => clearTimeout(timer))
}

// K2-3138: on the runtime cookie domain (config is merged with the page's at client start)
function cookieDomain () {
  return (config.cookies && config.cookies.domain) || undefined
}

function writeCut (token) {
  writeAccessTokenCookie(Vue.$cookies, token, cookieDomain())
}

function removeCut () {
  removeAccessTokenCookie(Vue.$cookies, cookieDomain())
}

function readCut () {
  return Vue.$cookies.get('cut') || null
}

/**
 * K2-3138: a token for another customer (another tab signed in as someone else) is never taken:
 * this tab reloads to show the browser's current customer. The browser's session (cut, rut, cart)
 * is that customer's and stays: no logout (integration QA #6). A pair this tab's own refresh got
 * is that session's current one (vuestorefront-api rotated its rut into the cookie), so it goes to cut.
 */
function refuseOtherCustomer (context, token, start, issuedHere = false) {
  if (isServer || !isOtherCustomer(token, start.token)) {
    return false
  }
  endSessionGeneration() // nothing this tab still has in flight may write its customer back
  if (issuedHere) {
    writeCut(token)
  }
  reloadPage()
  return true
}

function takeToken (context, token, meta = undefined) {
  rootStore.state.userTokenInvalidateLock = 0 // TaskQueue may run again with the new token
  context.commit(types.USER_TOKEN_CHANGED, meta === undefined ? { newToken: token } : { newToken: token, meta })
}

function adoptToken (context, token, start) {
  if (refuseOtherCustomer(context, token, start)) {
    throw refreshFailure(409, 'Signed in as another customer in another tab')
  }
  takeToken(context, token)
  return { code: 200, result: token }
}

/** fetch + JSON, given up (and aborted) after REFRESH_TIMEOUT_MS. */
function postRefresh (url, init) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer
  const timedOut = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      if (controller) controller.abort()
      reject(new Error('The refresh timed out'))
    }, REFRESH_TIMEOUT_MS)
  })
  const answered = fetch(url, controller ? Object.assign({}, init, { signal: controller.signal }) : init)
    .then(res => res.json().catch(() => ({ code: res.status || 0, result: 'Invalid refresh response' })))
  return Promise.race([answered, timedOut]).then(
    resp => { clearTimeout(timer); return resp },
    err => { clearTimeout(timer); throw err }
  )
}

/** The cut another tab wrote after this refresh started, if any. */
async function winnerCut (startCut) {
  let cut = readCut()
  if (!cut || cut === startCut) {
    await new Promise(resolve => setTimeout(resolve, WINNER_COOKIE_WAIT_MS))
    cut = readCut()
  }
  return cut && cut !== startCut ? cut : null
}

// start: { cut, token, logins } when the refresh was asked for
async function runRefresh (context, start) {
  const generation = sessionGeneration()
  if (!isServer) {
    const cut = readCut()
    // another tab refreshed while this one waited for the lock, or the browser is another customer's
    // now: never refresh then, the browser's rut is theirs (integration QA #6)
    if (cut && (cut !== start.cut || isOtherCustomer(cut, start.token))) {
      return adoptToken(context, cut, start)
    }
  }
  // K2-3138: the browser sends the rut cookie itself; SSR has it from the request cookies
  let body = isServer ? { refreshToken: rootStore.state.user.refreshToken } : {}
  let url = getApiEndpointUrl(config.users, 'refresh_endpoint')
  if (config.storeViews.multistore) {
    url = adjustMultistoreApiUrl(url)
  }
  let resp
  try {
    resp = await postRefresh(processURLAddress(url), refreshTokenTransport({
      method: 'POST',
      mode: 'cors',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }))
  } catch (err) {
    throw refreshFailure(0, err && err.message ? err.message : String(err))
  }
  const code = resp && resp.code !== undefined ? Number(resp.code) : 0
  if (generation !== sessionGeneration()) {
    // logged out meanwhile: drop the answer, and revoke what it issued unless someone signed in since
    // (a login generation: state.token is still set while a logout awaits its own revoke)
    if (code === 200 && !isServer && loginGeneration() === start.logins) {
      revokeSession(resp.result)
    }
    throw refreshFailure(code, 'The session ended during the refresh')
  }
  if (code === 200) {
    if (refuseOtherCustomer(context, resp.result, start, true)) {
      throw refreshFailure(409, 'Signed in as another customer in another tab')
    }
    takeToken(context, resp.result, resp.meta)
    if (!isServer) {
      writeCut(resp.result)
    }
    return resp
  }
  if (!isDefinitiveRefreshCode(code)) {
    throw refreshFailure(code, resp && resp.result)
  }
  if (!isServer) {
    const cut = await winnerCut(start.cut)
    if (cut) {
      return adoptToken(context, cut, start) // another tab won the rotation
    }
  }
  context.commit(types.USER_TOKEN_CHANGED, { newToken: null, meta: null })
  if (!isServer) {
    removeCut()
  }
  throw refreshFailure(code, resp.result, true)
}

const actions: ActionTree<UserState, RootState> = {
  async startSession (context) {
    if (isServer || context.getters.isLocalDataLoaded) return
    const cache = Vue.prototype.$db.usersCollection
    let newToken = context.getters.getUserToken
    if (newToken) {
      const user = await cache.getItem(`current-user`)
      if (user) {
        context.commit(types.USER_INFO_LOADED, user)
      }
      context.commit(types.USER_START_SESSION)
      context.commit(types.USER_LOCAL_DATA_LOADED, true)

      context.commit(types.USER_TOKEN_CHANGED, { newToken: newToken })
      context.dispatch('sessionAfterAuthorized')
      if (newToken !== Vue.$cookies.get('cut')) {
        writeCut(newToken)
      }
      Vue.prototype.$bus.$emit('session-after-started')
    } else {
      context.commit(types.USER_INFO_LOADED, null)
      context.commit(types.USER_START_SESSION)
      context.commit(types.USER_LOCAL_DATA_LOADED)
      Vue.prototype.$bus.$emit('session-after-nonauthorized')
    }
  },
  /**
   * Send password reset link for specific e-mail
   */
  resetPassword (context, { email }) {
    return TaskQueue.execute({ url: getApiEndpointUrl(config.users, 'resetPassword_endpoint'),
      payload: {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json, text/plain, */*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email })
      }
    })
  },
  /**
   * Login user and return user profile and current token
   */
  login (context, { email, password, quoteId = null }) {
    let url = getApiEndpointUrl(config.users, 'login_endpoint')
    if (config.storeViews.multistore) {
      url = adjustMultistoreApiUrl(url)
    }
    return fetch(processURLAddress(url), refreshTokenTransport({ method: 'POST',
      mode: 'cors',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: email, password: password, quoteId })
    }))
      .then(resp => resp.json())
      .then(resp => {
        if (resp.code === 200) {
          startLoginGeneration() // K2-3138: a stale refresh must not revoke this new session
          rootStore.state.userTokenInvalidateLock = 0
          context.commit(types.USER_TOKEN_CHANGED, { newToken: resp.result, meta: resp.meta }) // TODO: handle the "Refresh-token" header
          if (!isServer) {
            writeCut(resp.result)
            Vue.prototype.$bus.$emit('user-after-loggedin', resp.result)
          }
          context.dispatch('me', { refresh: true, useCache: false })
          // context.dispatch('getOrdersHistory', { refresh: true, useCache: false }).then(result => {})
        }
        return resp
      })
  },
  /**
   * Login user and return user profile and current token
   */
  async register (context, { email, firstname, lastname, password, phone, quoteId }) {
    let url = getApiEndpointUrl(config.users, 'create_endpoint')
    if (config.storeViews.multistore) {
      url = adjustMultistoreApiUrl(url)
    }
    return fetch(processURLAddress(url), refreshTokenTransport({
      method: 'POST',
      mode: 'cors',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customer: { email: email, firstname: firstname, lastname: lastname, telephone: phone }, password: password, quoteId })
    }))
      .then(resp => resp.json())
  },

  /**
   * K2-3138: refreshes the access token once for all callers (and tabs). Resolves {code: 200, result};
   * rejects with a RefreshFailure whose `definitive` says whether the session is over (400/401).
   * failedToken: the token the caller's request was rejected with.
   */
  refresh (context, { failedToken = null } = {}) {
    if (failedToken && context.state.token && context.state.token !== failedToken) {
      return Promise.resolve({ code: 200, result: context.state.token }) // already refreshed
    }
    const start = { cut: isServer ? null : readCut(), token: context.state.token, logins: loginGeneration() }
    return sharedRefresh(() => isServer ? runRefresh(context, start) : withRefreshLock(() => runRefresh(context, start)))
  },
  /**
   * Update user groupToken and groupId in state
   * @param context
   * @param userData
   */
  setUserGroup (context, userData) {
    if (config.usePriceTiers) {
      if (userData.groupToken) {
        context.commit(types.USER_GROUP_TOKEN_CHANGED, userData.groupToken)
      }

      if (userData.group_id) {
        context.commit(types.USER_GROUP_CHANGED, userData.group_id)
      }
    } else {
      context.commit(types.USER_GROUP_TOKEN_CHANGED, '')
      context.commit(types.USER_GROUP_CHANGED, null)
    }
  },
  /**
   * Load current user profile
   */
  me (context, { refresh = true, useCache = true } = {}) {
    return new Promise((resolve, reject) => {
      if (!context.state.token) {
        Logger.warn('No User token, user unauthorized', 'user')()
        return resolve(null)
      }

      let resolvedFromCache = false

      if (!isServer) {
        if (context.getters.getUserToken !== Vue.$cookies.get('cut')) {
          writeCut(context.getters.getUserToken)
        }
        if (useCache === true) { // after login for example we shouldn't use cache to be sure we're loading currently logged in user
          const cache = Vue.prototype.$db.usersCollection
          cache.getItem('current-user', (err, res) => {
            if (err) {
              Logger.error(err, 'user')()
              return
            }

            if (res) {
              Vue.prototype.$db.checkoutFieldsCollection.getItem('personal-details', (err, details) => {
                if (err) throw new Error(err)
                if (details && details.hasOwnProperty('phoneNumber')) {
                  res.phoneNumber = details.phoneNumber
                }
                context.commit(types.USER_INFO_LOADED, res)
                context.dispatch('setUserGroup', res)
                Vue.prototype.$bus.$emit('user-after-loggedin', res)
                rootStore.dispatch('cart/authorize')

                resolve(res)
                resolvedFromCache = true
                Logger.log('Current user served from cache', 'user')()
              })
            }
          })
        }
      }

      if (refresh) {
        customerRequest(getApiEndpointUrl(config.users, 'me_endpoint'))
          .then(async (resp: any) => {
            if (resp && resp.code === 200) {
              context.commit(types.USER_INFO_LOADED, resp.result) // this also stores the current user to localForage
              context.dispatch('setUserGroup', resp.result)
            }
            if (resp && !resolvedFromCache && resp.code === 200) {
              Vue.prototype.$bus.$emit('user-after-loggedin', resp.result)
              resolve(resp.result)
            } else {
              resolve(null)
            }
            resolve(resp)
          })
          .catch(err => {
            console.error('Error during loading the customer: ', err)
            reject(err)
          })
      } else {
        if (!resolvedFromCache) {
          resolve(null)
        }
      }
    })
  },
  /**
   * Update user profile with data from My Account page
   */
  async update (context, userData: UserProfile) {
    await TaskQueue.queue({
      url: getApiEndpointUrl(config.users, 'me_endpoint'),
      payload: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify(userData)
      },
      callback_event: 'store:user/userAfterUpdate'
    })
  },
  updateDefaultLocation (context, newDefaultLocation) {
    if (!isServer) {
      Vue.$cookies.set('default_location_code', newDefaultLocation)
    }
    customerRequest(
      getApiEndpointUrl(config.users, 'me_update_default_location_endpoint'),
      'POST',
      { default_location_code: newDefaultLocation }
    )
      .catch(err => console.error(err))
  },
  setCurrentUser (context, userData) {
    context.commit(types.USER_INFO_LOADED, userData)
  },
  /**
   * Change user password
   */
  changePassword (context, passwordData) {
    return TaskQueue.execute({ url: getApiEndpointUrl(config.users, 'changePassword_endpoint'),
      // K2-3138 review round 3: ms-api ends every session, this one too, and answers a fresh pair (rut by cookie)
      payload: refreshTokenTransport({
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(passwordData)
      })
    }).then((resp: any) => {
      if (resp.code === 200) {
        rootStore.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Password has successfully been changed'),
          action1: { label: i18n.t('OK') }
        })

        if (typeof resp.result === 'string' && resp.result) {
          startLoginGeneration() // a refresh still in flight belongs to the ended session
          takeToken(context, resp.result)
          if (!isServer) {
            writeCut(resp.result)
          }
        } else {
          rootStore.dispatch('user/login', { // an older API without the pair
            email: context.state.current.email,
            password: passwordData.newPassword
          })
        }
      } else {
        rootStore.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t(resp.result),
          action1: { label: i18n.t('OK') }
        })
      }
    })
  },
  clearCurrentUser (context) {
    if (!isServer) {
      removeCut()
    }
    context.commit(types.USER_TOKEN_CHANGED, '')
    context.commit(types.USER_GROUP_TOKEN_CHANGED, '')
    context.commit(types.USER_GROUP_CHANGED, null)
    context.commit(types.USER_INFO_LOADED, null)
    context.dispatch('checkout/savePersonalDetails', {}, {root: true})
    context.dispatch('checkout/saveShippingDetails', {}, {root: true})
    context.dispatch('checkout/savePaymentDetails', {}, {root: true})
    context.dispatch('clearOrderHistory')
  },
  /**
   * Logout user
   */
  async logout ({ commit, dispatch, state }, { silent = false, revoke = false } = {}) {
    endSessionGeneration() // a refresh still in flight must not bring the session back
    if (revoke && !isServer) {
      // K2-3138: only a logout the customer started; an automatic one (after a failed refresh)
      // could otherwise revoke the session another tab has just refreshed.
      await revokeSession(state.token, LOGOUT_REVOKE_TIMEOUT_MS)
    }
    commit(types.USER_END_SESSION)
    if (!isServer) {
      clearRuntimeCaches()
    }
    dispatch('cart/disconnect', {}, { root: true })
      .then(() => { rootStore.dispatch('ipay-payment-user/clearCardsList') })
      .then(() => { dispatch('clearCurrentUser') })
      .then(() => {
        if (!isServer && window.FB && window.FB.getAccessToken()) {
          window.FB.logout()
        }
      })
      .then(() => { Vue.prototype.$bus.$emit('user-after-logout') })
      .then(() => { dispatch('cart/clear', { recreateAndSyncCart: true, clearOnServer: false }, { root: true }) })
      .then(() => {
        rootStore.dispatch('address/clearStateAfterLogout')
      })
    if (!silent) {
      rootStore.dispatch('notification/spawnNotification', {
        type: 'success',
        message: i18n.t("You're logged out"),
        action1: { label: i18n.t('OK') }
      })
    }
    return
  },
  /**
   * Load user's orders history
   */
  getOrdersHistory (context, { refresh = true, useCache = true, page = 1, oldLocationCode, locationCode }) {
    // TODO: Make it as an extension from users module
    return new Promise(async (resolve, reject) => {
      if (!context.state.token) {
        Logger.debug('No User token, user unathorized', 'user')()
        return resolve(null)
      }
      const cache = Vue.prototype.$db.ordersHistoryCollection
      let resolvedFromCache = false

      if (useCache === true) { // after login for example we shouldn't use cache to be sure we're loading currently logged in user
        cache.getItem('orders-history', (err, res) => {
          if (err) {
            Logger.error(err, 'user')()
            return
          }

          if (res) {
            context.commit(types.USER_ORDERS_HISTORY_LOADED, res)
            Vue.prototype.$bus.$emit('user-after-loaded-orders', res)

            resolve(res)
            resolvedFromCache = true
            Logger.log('Current user order history served from cache', 'user')()
          }
        })
      }

      if (refresh) {
        rootStore.dispatch('ui/OnAndOffLoader', true)
          customerRequest(
            getApiEndpointUrl(config.users, 'history_endpoint'),
            'GET',
            {
              location_code: locationCode,
              page: page
            }
          )
          .then(resp => {
            let response = typeof resp === 'object' ? resp.result : resp
            let res = {
              items: response.data,
              page: response.meta.current_page,
              last_page: response.meta.last_page,
              old_location: oldLocationCode,
              current_location: locationCode
            };
            context.commit(types.USER_ORDERS_HISTORY_LOADED, res) // this also stores the current user to localForage
            Vue.prototype.$bus.$emit('user-after-loaded-orders', res)

            if (!resolvedFromCache) {
              resolve(resp.code === 200 ? resp : null)
            }
            return resp
          })
          .catch(e => {
            console.error(e)
          })
          .finally(() => {
          rootStore.dispatch('ui/OnAndOffLoader', false)
        })
      } else {
        if (!resolvedFromCache) {
          resolve(null)
        }
      }
    })
  },

  async getOrderItems (context, orderId) {
    return await customerRequest(
        getApiEndpointUrl(config.users, 'order_history_items_endpoint'),
        'GET',
        { orderId }
        ).then(result => result.result.data);
  },

  async getOrderItemsForReorder (context, orderId) {
    return await customerRequest(
      getApiEndpointUrl(config.users, 'reorder_history_items_endpoint'),
      'GET',
      { orderId }
    ).then(result => result.result.data);
  },

  /** "Recently bought" section on a partner's page (K2-2999). */
  async getRecentlyBoughtFromPartner (context, { partnerCode, locationCode }) {
    return await customerRequest(
      getApiEndpointUrl(config.users, 'recently_bought_endpoint'),
      'GET',
      { partnerCode, location_code: locationCode }
    ).then(result => result.result.items);
  },

  async orderedLocations (context) {
    return await customerRequest(getApiEndpointUrl(config.users, 'orderedLocation_endpoint')).then(result => {
      let response = typeof result === 'object' ? result.result : result
      context.commit('setOrderedLocations', { locations: response })
      return response
    })
  },

  async requestPhoneVerification (context, phone_number = null) {
    let payload = phone_number ? { phone_number } : null
    return await customerRequest(getApiEndpointUrl(config.users, 'request_phone_verification_endpoint'), 'POST', payload)
  },

  async verifyPhoneNumber ({ commit }, code) {
    return await customerRequest(getApiEndpointUrl(config.users, 'verify_phone_number_endpoint'), 'POST', { code })
      .then(result => {
        if (result.result.status === 'success') {
          commit(types.USER_PHONE_NUMBER_VERIFIED)
        }
        return result
      })
  },

  async getOneOrder ({ commit }, orderId) {

    return await customerRequest(
        getApiEndpointUrl(config.users, 'order_endpoint'),
        'GET',
        { orderId }
    ).then(result => {
      if (result.code === 200) {
        commit('setOrder', { order: result.result })
        return result.result
      }
    });
  },

  clearOrderHistory (context) {
    context.commit('clearOrderHistory')
  },

  userAfterUpdate (context, event) {
    if (event.resultCode === 200) {
      rootStore.dispatch('notification/spawnNotification', {
        type: 'success',
        message: i18n.t('Account data has successfully been updated'),
        action1: { label: i18n.t('OK') }
      })
      rootStore.dispatch('user/setCurrentUser', event.result)
      Vue.prototype.$bus.$emit('update-telephone-value')
    } else {
      Vue.prototype.$bus.$emit('clear-user-date')
    }
  },
  sessionAfterAuthorized (context, event) {
    Logger.info('User session authorised ', 'user')()
    rootStore.dispatch('user/me', { refresh: navigator.onLine }, { root: true }).then((us) => {}) // this will load user cart
    // rootStore.dispatch('user/getOrdersHistory', { refresh: navigator.onLine }, { root: true }).then((us) => {})
  },
  addedAgeConfirmationProduct (context, product) {
    context.commit('addedAgeConfirmationProduct', product)
  },
  updateOrderLocally ({ commit }, payload) {
    commit(types.USER_ORDER_IN_ORDER_HISTORY_UPDATE, payload)
  },
  updateUserHeaders ({ commit }, payload) {
    commit(types.USER_HEADERS_UPDATE, payload)
  }
}

export default actions
